import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { AppLink } from '@/components/shared/AppLink';
import { PageContainer } from '@/components/shared/PageContainer';
import { RiskRulesTable } from '@/components/shared/RiskRulesTable';
import { riskRulesQueryOptions } from '@/hooks/useRiskRulesList';
import { queryClient } from '@/lib/utils/queryClient';
import { useRuleTemplatesTab } from '../../../-hooks/useRuleTemplatesTab';

const searchSchema = z.object({
  page: z.number().optional(),
  limit: z.number().optional(),
  sort: z.string().optional(),
  filters: z.record(z.string(), z.array(z.string())).optional(),
  search: z.string().optional(),
});

export const Route = createFileRoute(
  '/management/risk-management/(pages)/(list)/rule-templates/',
)({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    limit: search.limit ?? 50,
    ...(search.sort !== undefined ? { sort: search.sort } : {}),
    ...(search.filters !== undefined ? { filters: search.filters } : {}),
  }),
  // Swallowed on failure — see the risk-gross route.
  loader: ({ deps }) =>
    queryClient
      .ensureQueryData(riskRulesQueryOptions({ ...deps, view: 'RETRACED' }))
      .catch(() => undefined),
  component: RuleTemplatesTabPage,
});

function RuleTemplatesTabPage() {
  const tab = useRuleTemplatesTab();

  return (
    <PageContainer>
      <RiskRulesTable
        rows={tab.list.rows}
        total={tab.list.total}
        isLoading={tab.list.isLoading}
        myCompanyId={tab.myCompanyId}
        selectedIds={tab.selectedIds}
        onSelectionChange={tab.setSelectedIds}
        hasSelection={tab.hasSelection}
        canCreate
        canEdit
        showTemplateColumns
        // The name opens the template in management's own editor. Left to its
        // default it points at the client route, whose guard sends Retraced
        // staff to the company picker instead.
        renderTitle={rule => (
          <AppLink
            to='/management/risk-management/rule-templates/$id/edit'
            params={{ id: rule.id }}
            bold
          >
            {rule.title}
          </AppLink>
        )}
        sorting={tab.list.sorting}
        filters={tab.list.filters}
        pagination={tab.list.pagination}
        onSortingChange={tab.list.onSortingChange}
        onFiltersChange={tab.list.onFiltersChange}
        onPaginationChange={tab.list.onPaginationChange}
        onView={tab.onView}
        onEdit={tab.onEdit}
        onBulkClone={tab.handleBulkClone}
        isBulkClonePending={tab.isBulkClonePending}
        onSubmitCreate={tab.handleCreate}
        isCreatePending={tab.isCreatePending}
        onOpenHelp={tab.openHelp}
      />
    </PageContainer>
  );
}
